import React, { useState, useEffect, useContext } from 'react';
import { formatDateDMY } from '../utils/dateUtils';
import axios from 'axios';
import { apiUrl } from '../config/api';
import { AuthContext } from '../context/AuthContext';
import Button from '../components/UI/Button';
import ResponsiveTable from '../components/ResponsiveTable';
import StatusBadge from '../components/UI/StatusBadge';
import { useTableSort } from '../components/UI/tableSort';
import LoanLink from '../components/LoanLink';
import { toCurrency } from '../utils/adminMetrics';
import Pager from '../components/UI/Pager';

const PAGE_SIZE = 10;

const ClientContracts = () => {
  const { user } = useContext(AuthContext);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  // Row background by status
  const getRowBg = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'pending':
        return 'bg-blue-50';
      case 'approved':
        return 'bg-green-50';
      case 'awaiting client':
        return 'bg-indigo-50';
      case 'awaiting staff':
        return 'bg-purple-50';
      case 'outstanding':
        return 'bg-yellow-50';
      case 'paid off':
        return 'bg-emerald-50';
      case 'rejected':
      case 'cancelled':
        return 'bg-red-50';
      default:
        return '';
    }
  };

  useEffect(() => {
    fetchContracts();
  }, []);

  const fetchContracts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Fetch loans for the client
  const response = await axios.get(apiUrl('/api/loans/my-loans'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const loansWithContracts = await Promise.all(
        response.data.map(async (loan) => {
          try {
            // For each loan, check if it has a contract
            const contractResponse = await axios.head(
              apiUrl(`/api/contracts/${loan.id}/view`),
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            // If the contract exists (status 200), return the loan with hasContract=true
            if (contractResponse.status === 200) {
              return { ...loan, hasContract: true };
            }
            return { ...loan, hasContract: false };
          } catch (err) {
            return { ...loan, hasContract: false };
          }
        })
      );
      
      // Filter out loans without contracts
      setContracts(loansWithContracts.filter(loan => loan.hasContract));
      setError(null);
    } catch (err) {
      console.error('Error fetching contracts:', err);
      setError('Failed to fetch contracts. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const viewContract = async (loanId) => {
    const token = localStorage.getItem('token');
    if (!token) return setError('Authentication token not found. Please log in again.');
    try {
      const resp = await fetch(apiUrl(`/api/contracts/${loanId}/view`), { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) {
        const txt = await resp.text();
        return setError(`Failed to open contract: ${resp.status} ${txt}`);
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (e) {
      setError('Failed to open contract.');
    }
  };

  // Filter contracts based on search term
  const filteredContracts = contracts.filter(contract => {
    const searchable = [
      contract.id?.toString() || '',
      contract.status || '',
      contract.principal_amount?.toString() || '',
      contract.start_date || '',
      contract.due_date || ''
    ].join(' ').toLowerCase();
    
    return searchable.includes(searchTerm.toLowerCase());
  });

  // Pagination
  const totalPages = Math.ceil(filteredContracts.length / PAGE_SIZE);
  // The table's rows for every match, so a click on a column sorts them all
  // before the page is taken.
  const contractRows = filteredContracts.map(contract => ({
    ...contract,
    loanCodeDisplay: contract.loan_code || contract.loanCode || '-',
    amountDisplay: toCurrency(parseFloat(contract.principal_amount)),
    startDateDisplay: formatDateDMY(contract.start_date),
    dueDateDisplay: formatDateDMY(contract.due_date),
  }));
  const contractSort = useTableSort(contractRows);
  const paginatedContracts = contractSort.rows.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  return (
    <div className="max-w-full min-h-screen">
      
      {/* Search and Pagination */}
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="w-full sm:max-w-xs">
          <input
            type="text"
            placeholder="Search contracts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full p-2"
          />
        </div>
        
        <Pager page={currentPage} pages={totalPages} onPage={setCurrentPage} />
      </div>
      
      {/* Error message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}
      
      {/* Loading state */}
      {loading ? (
        <div className="flex items-center justify-center p-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
          <span className="ml-3 text-lg text-blue-700">Loading contracts...</span>
        </div>
      ) : (
        <ResponsiveTable
          sort={contractSort.sort}
          onSortChange={contractSort.onSortChange}
          data={paginatedContracts}
          columns={[
            { field: 'loanCodeDisplay', header: 'Loan Code', formatter: (value, row) => <LoanLink loanId={row.id}>{value}</LoanLink> },
            { field: 'amountDisplay', header: 'Amount', sortValue: (row) => Number(row.principal_amount) },
            { field: 'status', header: 'Status', formatter: value => (
                <StatusBadge status={value} />
              ) },
            { field: 'startDateDisplay', header: 'Start Date', sortValue: (row) => row.start_date },
            { field: 'dueDateDisplay', header: 'Due Date', sortValue: (row) => row.due_date },
            { field: 'actions', header: 'Actions', formatter: (_, row) => (
                <Button
                  variant="action"
                  onClick={() => viewContract(row.id)}
                >
                  View Contract
                </Button>
              ) }
          ]}
          keyField="id"
          emptyMessage="No contracts found matching your search criteria"
        />
      )}
    </div>
  );
};

export default ClientContracts;
